"use client"

import { HttpTypes } from "@medusajs/types"
import { isEqual } from "lodash"
import { useEffect, useMemo, useState } from "react"

export const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) => {
  return variantOptions?.reduce((acc: Record<string, string>, varopt) => {
    if (varopt.option_id) acc[varopt.option_id] = varopt.value
    return acc
  }, {})
}

export const isVariantInStock = (
  variant?: HttpTypes.StoreProductVariant
): boolean => {
  // If we don't manage inventory, we can always add to cart
  if (variant && !variant.manage_inventory) {
    return true
  }

  // If we allow back orders on the variant, we can add to cart
  if (variant?.allow_backorder) {
    return true
  }

  // If there is inventory available, we can add to cart
  if (variant?.manage_inventory && (variant?.inventory_quantity || 0) > 0) {
    return true
  }

  // Otherwise, we can't add to cart
  return false
}

// Shared option-selection state for PDP actions, Quick View and Quick Add:
// tracks the chosen option values, filters values to combinations that form
// an existing variant, and resolves the selected variant + stock status.
export function useOptionSelection(product: HttpTypes.StoreProduct) {
  const [options, setOptions] = useState<Record<string, string | undefined>>({})

  const variantOptionMaps = useMemo(
    () => (product.variants ?? []).map((v) => optionsAsKeymap(v.options) ?? {}),
    [product.variants]
  )

  // If there is only 1 variant, preselect the options
  useEffect(() => {
    if (product.variants?.length === 1) {
      const variantOptions = optionsAsKeymap(product.variants[0].options)
      setOptions(variantOptions ?? {})
    }
  }, [product.variants])

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return
    }

    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  // update the options when a variant is selected
  const setOptionValue = (optionId: string, value: string) => {
    setOptions((prev) => {
      const next: Record<string, string | undefined> = {
        ...prev,
        [optionId]: value,
      }

      // Clear previously selected values that no longer combine with the
      // rest of the selections into any existing variant (never swap them).
      let cleared = true
      while (cleared) {
        cleared = false

        for (const [id, selected] of Object.entries(next)) {
          if (id === optionId || selected == null) {
            continue
          }

          const others = Object.entries(next).filter(
            ([otherId, otherValue]) => otherId !== id && otherValue != null
          )

          const stillValid = variantOptionMaps.some(
            (variantOptions) =>
              variantOptions[id] === selected &&
              others.every(
                ([otherId, otherValue]) =>
                  variantOptions[otherId] === otherValue
              )
          )

          if (!stillValid) {
            delete next[id]
            cleared = true
          }
        }
      }

      return next
    })
  }

  // For each option, the values that form an existing variant together with
  // every value currently selected on the OTHER options. With nothing else
  // selected this is every value that appears in at least one variant.
  const visibleOptionValues = useMemo(() => {
    const result: Record<string, string[]> = {}

    for (const option of product.options ?? []) {
      const otherSelections = Object.entries(options).filter(
        ([id, value]) => id !== option.id && value != null
      )

      result[option.id] = (option.values ?? [])
        .map((v) => v.value)
        .filter((value) =>
          variantOptionMaps.some(
            (variantOptions) =>
              variantOptions[option.id] === value &&
              otherSelections.every(
                ([otherId, otherValue]) =>
                  variantOptions[otherId] === otherValue
              )
          )
        )
    }

    return result
  }, [product.options, variantOptionMaps, options])

  //check if the selected options produce a valid variant
  const isValidVariant = useMemo(() => {
    return product.variants?.some((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  // check if the selected variant is in stock
  const inStock = useMemo(
    () => isVariantInStock(selectedVariant),
    [selectedVariant]
  )

  return {
    options,
    setOptionValue,
    selectedVariant,
    visibleOptionValues,
    isValidVariant,
    inStock,
  }
}
